import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send, CheckCircle, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { saveToLocalStorage, loadFromLocalStorage, mockContactSubmissions } from '../data/mockData';
import AnimatedText from '../components/AnimatedText';
import FloatingCard from '../components/FloatingCard';
import ShimmerButton from '../components/ShimmerButton';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    if (!formData.firstName || !formData.lastName || !formData.email || !formData.message) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs obligatoires.",
        variant: "destructive"
      });
      setIsSubmitting(false);
      return;
    }

    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const existingSubmissions = loadFromLocalStorage('soleil_contact_submissions', mockContactSubmissions);
      const newSubmission = {
        id: Date.now(),
        ...formData,
        submittedAt: new Date().toISOString(),
        status: 'pending'
      };
      
      const updatedSubmissions = [...existingSubmissions, newSubmission];
      saveToLocalStorage('soleil_contact_submissions', updatedSubmissions);
      
      toast({
        title: "Message envoyé avec succès !",
        description: "Nous vous répondrons dans les 24 heures.",
      });
      
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
      
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Échec de l'envoi du message. Veuillez réessayer.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-900">
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-20">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(251,191,36,0.1),transparent_70%)]"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <AnimatedText 
              className="text-4xl sm:text-5xl font-bold text-white mb-6"
              isH1={true}
            >
              Contactez <span className="title-gradient">Nous</span>
            </AnimatedText>
            <AnimatedText 
              className="text-xl text-gray-300 leading-relaxed font-inter"
              delay={200}
            >
              Prêt à illuminer votre marque ? Discutons de la façon dont nous pouvons vous aider à atteindre vos objectifs de communication.
            </AnimatedText>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            <div className="space-y-8">
              <AnimatedText>
                <div>
                  <h2 className="text-3xl font-inter font-bold text-white mb-4">Commençons une Conversation</h2>
                  <p className="text-gray-400 leading-relaxed font-inter">
                    Nous sommes là pour vous aider à concrétiser votre vision. Que vous recherchiez une refonte complète de marque ou des solutions de communication spécifiques, notre équipe est prête à collaborer avec vous.
                  </p>
                </div>
              </AnimatedText>

              <div className="space-y-6">
                <AnimatedText delay={200}>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-inter font-semibold text-white">Email</h3>
                      <p className="text-gray-400 font-inter">contact@soleil-azur.com</p>
                    </div>
                  </div>
                </AnimatedText>

                <AnimatedText delay={300}>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-inter font-semibold text-white">Téléphone</h3>
                      <p className="text-gray-400 font-inter">+33 6 43 84 00 01</p>
                    </div>
                  </div>
                </AnimatedText>

                <AnimatedText delay={400}>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-inter font-semibold text-white">Localisation</h3>
                      <p className="text-gray-400 font-inter">France, 11100 Narbonne</p>
                    </div>
                  </div>
                </AnimatedText>
              </div>

              <AnimatedText delay={500}>
                <FloatingCard className="bg-gray-800 border border-gray-700" delay={0.3}>
                  <div className="p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <Clock className="w-6 h-6 text-amber-500" />
                      <h3 className="text-lg font-inter font-semibold text-white">Temps de Réponse</h3>
                    </div>
                    <div className="flex items-center space-x-2 text-green-400">
                      <CheckCircle className="w-5 h-5" />
                      <span className="font-inter font-medium">Réponse sous 24 h</span>
                    </div>
                  </div>
                </FloatingCard>
              </AnimatedText>
            </div>

            <AnimatedText delay={600}>
              <FloatingCard className="bg-gray-800 border border-gray-700 shadow-xl" delay={0.5}>
                <CardHeader>
                  <CardTitle className="text-2xl text-white font-inter">Envoyez-nous un Message</CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="firstName" className="text-sm font-medium text-gray-300 font-inter">
                          Prénom *
                        </Label>
                        <Input
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          required
                          className="mt-1 font-inter bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName" className="text-sm font-medium text-gray-300 font-inter">
                          Nom *
                        </Label>
                        <Input
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          required
                          className="mt-1 font-inter bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-sm font-medium text-gray-300 font-inter">
                        Adresse Email *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="mt-1 font-inter bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone" className="text-sm font-medium text-gray-300 font-inter">
                        Numéro de Téléphone
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="mt-1 font-inter bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      />
                    </div>

                    <div>
                      <Label htmlFor="subject" className="text-sm font-medium text-gray-300 font-inter">
                        Sujet
                      </Label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        className="mt-1 font-inter bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-sm font-medium text-gray-300 font-inter">
                        Message *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        required
                        rows={6}
                        className="mt-1 font-inter bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                        placeholder="Parlez-nous de votre projet..."
                      />
                    </div>

                    <ShimmerButton
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white py-6 text-lg rounded-full"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Envoyer le Message
                        </>
                      )}
                    </ShimmerButton>
                  </form>
                </CardContent>
              </FloatingCard>
            </AnimatedText>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <AnimatedText>
              <div>
                <h3 className="text-xl font-inter font-semibold text-white mb-4">Nouveaux Projets</h3>
                <p className="text-gray-400 font-inter">
                  Prêt à démarrer quelque chose de nouveau ? Nous aimerions connaître votre vision et discuter de la façon dont nous pouvons l&apos;aider à prendre vie.
                </p>
              </div>
            </AnimatedText>
            <AnimatedText delay={200}>
              <div>
                <h3 className="text-xl font-inter font-semibold text-white mb-4">Partenariats</h3>
                <p className="text-gray-400 font-inter">
                  Intéressé par un partenariat avec nous ? Nous sommes toujours ouverts à collaborer avec des professionnels partageant les mêmes idées.
                </p>
              </div>
            </AnimatedText>
            <AnimatedText delay={400}>
              <div>
                <h3 className="text-xl font-inter font-semibold text-white mb-4">Demandes Générales</h3>
                <p className="text-gray-400 font-inter">
                  Vous avez des questions sur nos services ou vous voulez simplement dire bonjour ? Nous sommes là pour vous aider avec tout ce dont vous avez besoin.
                </p>
              </div>
            </AnimatedText>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
